import React, { useState, useEffect } from 'react';
import { Shield, Droplets, Sun, Sparkles, Wind, Leaf, Clock, Brush, CheckCircle2, Calculator, Phone, ShoppingCart, ChevronRight, Zap, AlertCircle, Play, Star, Award, ThumbsUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { useConsumptionCalculator } from '../hooks/useConsumptionCalculator';

// Imagem do produto
const PRODUCT_IMAGE = 'https://customer-assets.emergentagent.com/job_76e4af31-79af-416e-8105-b97c6751d37e/artifacts/fspx3y3f_elastimper-product.jpg';

// GIFs do produto em ação
const GIFS = [
  {
    url: 'https://customer-assets.emergentagent.com/job_76e4af31-79af-416e-8105-b97c6751d37e/artifacts/q2d87qqp_lv_0_20260222143524.gif',
    title: 'Fácil Limpeza',
    description: 'A sujeira simplesmente não adere'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_76e4af31-79af-416e-8105-b97c6751d37e/artifacts/gtky7ldx_lv_0_20260222143650.gif',
    title: 'Proteção Total',
    description: 'Madeira sempre protegida'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_76e4af31-79af-416e-8105-b97c6751d37e/artifacts/mk6ad9f1_lv_0_20260222143846.gif',
    title: 'Telhas Impermeáveis',
    description: 'Água escorre instantaneamente'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_76e4af31-79af-416e-8105-b97c6751d37e/artifacts/97u5fsoq_lv_0_20260222144054.gif',
    title: 'Efeito Lótus',
    description: 'Tecnologia que impressiona'
  }
];

const UVBlockLandingV3 = () => {
  const { area, setArea, consumption, calculateConsumption } = useConsumptionCalculator();
  const [activeGif, setActiveGif] = useState(0);

  // Auto-rotate GIFs
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveGif((prev) => (prev + 1) % GIFS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const benefits = [
    { icon: Droplets, title: 'Efeito Lótus', description: 'Silano que repele água com poder máximo', highlight: true },
    { icon: Sun, title: 'Proteção UV', description: 'Cores preservadas por anos', highlight: true },
    { icon: Leaf, title: 'Anti-Mofo', description: 'Elimina fungos definitivamente', highlight: true },
    { icon: Sparkles, title: 'Anti-Sujeira', description: 'Superfície sempre limpa', highlight: true },
    { icon: Shield, title: 'Resistência', description: 'Suporta submersão em água' },
    { icon: Wind, title: 'Fácil Limpeza', description: 'Sujeira escorre com a água' },
    { icon: Clock, title: 'Durabilidade', description: 'Proteção de longa duração' },
    { icon: Brush, title: 'Versatilidade', description: 'Madeira, pedra, concreto' },
    { icon: CheckCircle2, title: 'Base Água', description: 'Ecológico e seguro' },
    { icon: Zap, title: 'Aglutinador', description: 'Fortalece superfícies' }
  ];

  const videos = [
    { id: '07LYvaHcMpA', title: 'Efeito Lótus - Repelência Extrema' },
    { id: 'tGTXi3_KfQc', title: 'UVBLOCK 10 em 1' },
    { id: 'CG7Evabu8ws', title: 'Aplicação e Resultados' }
  ];

  const faqs = [
    {
      question: 'Como o UVBLOCK elimina mofo do teto do banheiro?',
      answer: 'O UVBLOCK possui ação anti-fúngica que impede a proliferação de fungos e bactérias. O efeito hidrorrepelente do silano siloxano evita que a umidade penetre na superfície, eliminando o ambiente propício para o desenvolvimento de mofo.'
    },
    {
      question: 'O que é o "Efeito Lótus" e como funciona?',
      answer: 'Inspirado nas folhas da flor de lótus, o silano siloxano molecular cria uma camada que faz a água formar gotas e escorrer, levando a sujeira junto. Isso mantém a superfície sempre limpa e protegida.'
    },
    {
      question: 'Como o filtro UV preserva as cores?',
      answer: 'O UVBLOCK é o único com filtro UV integrado na formulação. Ele bloqueia os raios ultravioleta que causam desbotamento, mantendo as cores vibrantes por muito mais tempo.'
    },
    {
      question: 'Posso usar em áreas molhadas como piscinas?',
      answer: 'Sim! O UVBLOCK possui altíssima resistência à água e pode ser usado em áreas de contato constante com água como banheiros, box e piscinas.'
    }
  ];

  const whatsappNumber = '5511911601122';
  const whatsappMessage = encodeURIComponent('Olá! Quero proteger minhas superfícies com o UVBLOCK!');

  return (
    <div className="min-h-screen bg-white">
      {/* Header Fixo */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-[#0B5A7D] to-[#003D5C] rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">UVBLOCK</span>
          </div>
          <div className="hidden md:flex items-center space-x-6">
            <a href="#resultados" className="text-gray-700 hover:text-[#0B5A7D] transition-colors font-medium">Resultados</a>
            <a href="#beneficios" className="text-gray-700 hover:text-[#0B5A7D] transition-colors font-medium">Benefícios</a>
            <a href="#calculadora" className="text-gray-700 hover:text-[#0B5A7D] transition-colors font-medium">Calculadora</a>
            <Button 
              className="bg-gradient-to-r from-[#0B5A7D] to-[#003D5C] hover:from-[#003D5C] hover:to-[#002a40] shadow-lg"
              onClick={() => window.location.href = 'https://elastimper.ind.br/categoria-produto/resina-multiuso/'}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Comprar
            </Button>
          </div>
        </div>
      </header>

      {/* HERO SECTION - Impactante e Emocional */}
      <section className="pt-20 relative overflow-hidden">
        {/* Background animado */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B5A7D] via-[#003D5C] to-black">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full filter blur-[100px] animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400 rounded-full filter blur-[120px] animate-pulse" style={{animationDelay: '1s'}}></div>
          </div>
        </div>

        <div className="relative z-10 container mx-auto px-4 py-12">
          {/* Badge de destaque */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 bg-orange-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg animate-bounce">
              <Star className="w-4 h-4" />
              MAIS DE 10.000 CLIENTES SATISFEITOS
              <Star className="w-4 h-4" />
            </div>
          </div>

          {/* Título Principal com Impacto */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-4">
              CHEGA DE <span className="text-orange-400">MOFO!</span>
              <br />
              <span className="text-3xl md:text-5xl lg:text-6xl font-bold text-blue-200">
                Chega de superfícies manchadas!
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mt-6 leading-relaxed">
              Descubra a <span className="text-orange-400 font-bold">única resina com Silano + Filtro UV</span> que 
              transforma qualquer superfície em uma barreira impenetrável contra água, mofo e sujeira!
            </p>
          </div>

          {/* GIF Principal em Destaque */}
          <div className="max-w-5xl mx-auto mb-8">
            <div className="relative">
              {/* GIF Principal Grande */}
              <div className="bg-black/30 backdrop-blur-sm rounded-3xl p-4 shadow-2xl border border-white/10">
                <div className="aspect-video rounded-2xl overflow-hidden relative">
                  <img 
                    src={GIFS[activeGif].url}
                    alt={GIFS[activeGif].title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                    <h3 className="text-2xl md:text-3xl font-bold text-white">{GIFS[activeGif].title}</h3>
                    <p className="text-lg text-gray-200">{GIFS[activeGif].description}</p>
                  </div>
                </div>
              </div>

              {/* Miniaturas dos GIFs */}
              <div className="flex justify-center gap-3 mt-4">
                {GIFS.map((gif, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveGif(index)}
                    className={`w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-3 transition-all duration-300 ${
                      activeGif === index 
                        ? 'border-orange-500 scale-110 shadow-lg shadow-orange-500/50' 
                        : 'border-white/30 opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={gif.url} alt={gif.title} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              size="lg" 
              className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-10 py-7 rounded-full shadow-2xl shadow-orange-500/50 transform hover:scale-105 transition-all"
              onClick={() => window.location.href = 'https://elastimper.ind.br/categoria-produto/resina-multiuso/'}
            >
              <ShoppingCart className="w-6 h-6 mr-2" />
              QUERO PROTEGER AGORA
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white/10 text-xl px-10 py-7 rounded-full"
              onClick={() => window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank')}
            >
              <Phone className="w-6 h-6 mr-2" />
              FALAR COM ESPECIALISTA
            </Button>
          </div>

          {/* Badges de Confiança */}
          <div className="flex flex-wrap justify-center gap-6 text-white/80">
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span>Frete Grátis +R$1.000</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span>5% Desconto PIX</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <Award className="w-5 h-5 text-yellow-400" />
              <span>Garantia de Qualidade</span>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Seção: Resultados em GIFs */}
      <section id="resultados" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-orange-500 font-bold text-sm uppercase tracking-wider">Veja com seus próprios olhos</span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mt-2">
              Resultados <span className="text-[#0B5A7D]">Impressionantes</span>
            </h2>
            <p className="text-xl text-gray-600 mt-4 max-w-2xl mx-auto">
              O poder do UVBLOCK em ação - proteção que você pode ver funcionando!
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {GIFS.map((gif, index) => (
              <div 
                key={index} 
                className="group bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-gray-100 hover:border-[#0B5A7D]"
              >
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={gif.url}
                    alt={gif.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 bg-gradient-to-br from-gray-50 to-white">
                  <h3 className="font-bold text-xl text-gray-900 mb-1">{gif.title}</h3>
                  <p className="text-gray-600">{gif.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button 
              size="lg" 
              className="bg-[#0B5A7D] hover:bg-[#003D5C] text-lg px-8 py-6 rounded-full shadow-xl"
              onClick={() => window.location.href = 'https://elastimper.ind.br/categoria-produto/resina-multiuso/'}
            >
              <ThumbsUp className="w-5 h-5 mr-2" />
              Quero Esses Resultados!
            </Button>
          </div>
        </div>
      </section>

      {/* Seção: Vídeos */}
      <section id="videos" className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-orange-400 font-bold text-sm uppercase tracking-wider">Vídeos Demonstrativos</span>
            <h2 className="text-4xl md:text-5xl font-black mt-2">
              Assista e <span className="text-orange-400">Comprove</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {videos.map((video, index) => (
              <div key={index} className="bg-gray-800 rounded-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300 shadow-xl">
                <div className="aspect-video">
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-lg">{video.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Seção: Produto + Diferenciais */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Imagem do Produto */}
            <div className="relative">
              <div className="bg-gradient-to-br from-[#0B5A7D] to-[#003D5C] rounded-3xl p-8 shadow-2xl">
                <img 
                  src={PRODUCT_IMAGE}
                  alt="UVBLOCK Resina Multiuso"
                  className="w-full max-w-sm mx-auto drop-shadow-2xl transform hover:scale-105 hover:rotate-2 transition-all duration-500"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-orange-500 text-white px-6 py-3 rounded-2xl font-bold text-xl shadow-xl">
                10 em 1
              </div>
            </div>

            {/* Diferenciais */}
            <div>
              <span className="text-[#0B5A7D] font-bold text-sm uppercase tracking-wider">Por que escolher UVBLOCK?</span>
              <h2 className="text-4xl font-black text-gray-900 mt-2 mb-8">
                3 Tecnologias <span className="text-orange-500">Exclusivas</span>
              </h2>
              
              <div className="space-y-6">
                <div className="flex gap-4 p-5 bg-white rounded-2xl shadow-lg border-l-4 border-[#0B5A7D] hover:shadow-xl transition-shadow">
                  <div className="w-14 h-14 bg-[#0B5A7D] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Droplets className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900">Efeito Lótus (Silano)</h3>
                    <p className="text-gray-600">Repelência molecular extrema - a água simplesmente não adere!</p>
                  </div>
                </div>

                <div className="flex gap-4 p-5 bg-white rounded-2xl shadow-lg border-l-4 border-orange-500 hover:shadow-xl transition-shadow">
                  <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Sun className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900">Filtro UV Integrado</h3>
                    <p className="text-gray-600">Cores vibrantes por anos - protege contra desbotamento solar!</p>
                  </div>
                </div>

                <div className="flex gap-4 p-5 bg-white rounded-2xl shadow-lg border-l-4 border-green-500 hover:shadow-xl transition-shadow">
                  <div className="w-14 h-14 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Leaf className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900">Anti-Mofo Total</h3>
                    <p className="text-gray-600">Elimina fungos de vez - perfeito para tetos de banheiro!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seção: 10 em 1 */}
      <section id="beneficios" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-[#0B5A7D] font-bold text-sm uppercase tracking-wider">Produto Completo</span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mt-2">
              UVBLOCK <span className="text-orange-500">10 em 1</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card 
                key={index} 
                className={`group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${
                  benefit.highlight ? 'border-2 border-[#0B5A7D] bg-blue-50' : 'border hover:border-[#0B5A7D]'
                }`}
              >
                <CardContent className="p-4 text-center">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 transition-colors ${
                    benefit.highlight ? 'bg-[#0B5A7D]' : 'bg-gray-100 group-hover:bg-[#0B5A7D]'
                  }`}>
                    <benefit.icon className={`w-6 h-6 transition-colors ${
                      benefit.highlight ? 'text-white' : 'text-[#0B5A7D] group-hover:text-white'
                    }`} />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm">{benefit.title}</h3>
                  <p className="text-xs text-gray-600 mt-1">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Calculadora */}
      <section id="calculadora" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <Calculator className="w-16 h-16 text-[#0B5A7D] mx-auto mb-4" />
              <h2 className="text-3xl font-black text-gray-900">
                Calcule seu Consumo
              </h2>
              <p className="text-gray-600 mt-2">Rendimento: ~80g/m² por demão</p>
            </div>
            
            <Card className="shadow-xl border-2 border-gray-200">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Área a ser aplicada (m²)
                    </label>
                    <Input
                      type="number"
                      placeholder="Ex: 50"
                      value={area}
                      onChange={(e) => setArea(e.target.value)}
                      className="text-lg p-5 border-2"
                    />
                  </div>
                  <Button 
                    onClick={calculateConsumption}
                    className="w-full bg-[#0B5A7D] hover:bg-[#003D5C] text-lg py-5"
                    disabled={!area || parseFloat(area) <= 0}
                  >
                    <Calculator className="w-5 h-5 mr-2" />
                    Calcular
                  </Button>
                  
                  {consumption && (
                    <div className="mt-4 p-5 bg-blue-50 rounded-xl space-y-3">
                      <div className="text-center">
                        <div className="text-sm text-gray-600">Consumo Total</div>
                        <div className="text-3xl font-black text-[#0B5A7D]">{consumption.totalKg} kg</div>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg border-2 border-[#0B5A7D]">
                        <div className="text-sm font-semibold text-[#0B5A7D] mb-2">Recomendação:</div>
                        {consumption.buckets36 > 0 && consumption.buckets18 === 0 && (
                          <div className="text-xl font-bold text-gray-900">
                            {consumption.buckets36} Balde(s) de 3,6L
                          </div>
                        )}
                        {consumption.buckets18 > 0 && consumption.buckets36 === 0 && (
                          <div className="text-xl font-bold text-gray-900">
                            {consumption.buckets18} Balde(s) de 18L
                          </div>
                        )}
                        {consumption.buckets18 > 0 && consumption.buckets36 > 0 && (
                          <div className="text-xl font-bold text-gray-900">
                            {consumption.buckets18} de 18L + {consumption.buckets36} de 3,6L
                          </div>
                        )}
                      </div>
                      
                      <Button 
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={() => window.location.href = 'https://elastimper.ind.br/categoria-produto/resina-multiuso/'}
                      >
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Comprar Agora
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-black text-gray-900">Dúvidas Frequentes</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-gray-50 rounded-xl border-2 border-gray-200 px-5"
                >
                  <AccordionTrigger className="text-left font-bold text-gray-900 hover:text-[#0B5A7D] py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-br from-[#0B5A7D] via-[#003D5C] to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-72 h-72 bg-orange-500 rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-400 rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-black mb-4">
            Transforme Suas Superfícies <span className="text-orange-400">Hoje!</span>
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Proteção profissional com tecnologia exclusiva. Resultados que você vai ver e sentir!
          </p>
          <Button 
            size="lg"
            className="bg-orange-500 hover:bg-orange-600 text-xl px-10 py-7 rounded-full shadow-2xl shadow-orange-500/50 transform hover:scale-105 transition-all"
            onClick={() => window.location.href = 'https://elastimper.ind.br/categoria-produto/resina-multiuso/'}
          >
            <ShoppingCart className="w-6 h-6 mr-2" />
            COMPRAR UVBLOCK AGORA
          </Button>
          <div className="mt-8 flex flex-wrap justify-center gap-6">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span>Frete Grátis +R$1.000</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span>5% Desconto PIX</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span>Parcelamento 12x</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-[#0B5A7D] rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold">UVBLOCK</span>
            </div>
            <div className="text-gray-400 text-sm">
              WhatsApp: +55 11 91160-1122 | Suporte técnico vitalício
            </div>
            <div className="text-gray-500 text-sm">
              © 2025 Elastimper. Todos os direitos reservados.
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Flutuante */}
      <a
        href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full shadow-2xl flex items-center justify-center z-50 transition-all hover:scale-110 animate-bounce"
        style={{animationDuration: '2s'}}
      >
        <Phone className="w-8 h-8 text-white" />
      </a>
    </div>
  );
};

export default UVBlockLandingV3;
